import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { margin } from "polished";
import React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { BynderAsset } from "@brexhq/next/src/components/shared/BynderAsset/BynderAsset";
import { ImageBorderRadiusWrapper } from "@brexhq/next/src/components/shared/BynderAsset/Image/BynderImage";
import { Link } from "@brexhq/next/src/components/shared/Link";
import { blocksToText } from "@brexhq/shared/utils/sanity/blocksToText";

import type { INavModuleProps, ILinkHeroNavModule } from "../../types";

const ModuleLink = styled(Link, {
  shouldForwardProp: (prop) => !["isOutlined", "borderRadius"].includes(prop),
})<{
  isOutlined?: boolean;
  borderRadius: string | number;
}>(({ theme, isOutlined, borderRadius }) => ({
  margin: 0,
  borderRadius,
  display: "grid",
  gap: theme.spacing.LRG.xsml,
  transition: `background-color ${theme.animations.durationSlow}`,
  padding: isOutlined ? theme.spacing.LRG.xsml : theme.spacing.LRG.xxsml,
  border: isOutlined ? `1px solid ${theme.colors.border.default}` : 0,
  "&:hover": {
    background: theme.colors.border.default,
    "[data-name='IconContainer']": {
      color: theme.colors.accent.primary.background.default,
    },
    "span[data-name='Arrow']": {
      marginLeft: theme.spacing.m4,
      transition: `all ${theme.animations.durationSlow}`,
    },
  },
  [theme.breakpoints.nav.SML]: {
    ...margin(theme.spacing.SML.sml),
    padding: 0,
    border: 0,
    "&:hover": {
      background: "unset",
    },
  },
}));

const Hero = styled(BynderAsset)({
  width: "100%",
  height: 200,
  objectFit: "cover",
});

const HeroContainer = styled(ImageBorderRadiusWrapper)<{
  mediaPosition: "top" | "bottom";
  showOnMobile?: boolean;
}>(({ theme, mediaPosition, showOnMobile }) => ({
  order: mediaPosition === "top" ? 0 : 1,
  [theme.breakpoints.nav.SML]: {
    display: !showOnMobile ? "none" : undefined,
  },
}));

const Title = styled.div(({ theme }) => ({
  ...theme.typography.BodySmallSB,
  color: theme.colors.content.primary,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing.LRG.xsml / 2,
  [theme.breakpoints.nav.MED]: {
    fontSize: 12,
  },
}));

const Subtitle = styled.div(({ theme }) => ({
  ...theme.typography.BodySmallM,
  color: theme.colors.content.tertiary,
  [theme.breakpoints.nav.MED]: {
    fontSize: 12,
  },
}));

const IconContainer = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  color: theme.colors.content.primary,
  transition: `all ${theme.animations.durationSlow}`,
  svg: {
    height: 24,
    width: 24,
  },
}));

export default function LinkHero({
  icon,
  imagePosition = "top",
  link,
  media,
  modifier,
  showMediaOnMobile,
  subtitle,
  subtitleV2Raw,
  title,
}: INavModuleProps<ILinkHeroNavModule>) {
  const { borders } = useTheme();
  const subtitleContent =
    subtitleV2Raw && subtitleV2Raw?.length > 0
      ? blocksToText(subtitleV2Raw)
      : subtitle;
  return (
    <ModuleLink
      component="nav LinkHero module"
      modifier="unstyled"
      to={link?.to || link?.toExternal || link?.hash || "/"}
      noFollow={link.noFollow}
      label={title}
      tabIndex={-1}
      isOutlined={modifier === "outline"}
      borderRadius={
        media.mediaStyle?.customBorderRadius ||
        media.mediaStyle?.borderRadius ||
        borders.radius.default
      }
    >
      <HeroContainer
        mediaPosition={imagePosition}
        showOnMobile={showMediaOnMobile}
        mediaStyle={
          media.mediaStyle?.borderRadius
            ? media.mediaStyle
            : {
                borderRadius: "Custom",
                customBorderRadius: borders.radius.default,
              }
        }
      >
        <Hero asset={media.media} />
      </HeroContainer>
      <Container direction="column" gap="xxsml">
        <Title>
          {icon && (
            <IconContainer
              data-name="IconContainer"
              dangerouslySetInnerHTML={{ __html: icon.mono }}
            />
          )}
          <span>
            {title}
            <span
              data-name="Arrow"
              aria-hidden="true"
              style={{ whiteSpace: "nowrap" }}
            >
              {" "}
              &rarr;
            </span>
          </span>
        </Title>
        {subtitleContent && <Subtitle>{subtitleContent}</Subtitle>}
      </Container>
    </ModuleLink>
  );
}
